// Inventory breakdown charts share the native totals plan with sheet, creator and object cards.
import type { CachedCaster } from '../types';
import { carryWeightBadgeClasses, computedCreature } from '../read-helpers';
import { inventoryDisplayRows as inventoryRows, inventoryContents as contentsOf, inventoryEquippedSlot as equippedSlotId,
    isInventoryDisplayContainer as isContainerEntry, isIntegratedInventory as isIntegratedMonsterBit,
    inventoryOnHandRows as onHandRows, inventoryBreakdownNode as buildWeightNode,
    findInventoryDisplayRow as findRowById, inventoryRowTotals } from '../../janus-engine/display/inventory-totals-21';
import type { InventoryDisplayRow as InventoryEntry } from '../../janus-engine/display/inventory-totals-21';

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const ST_LOCKED = '477A7977-60D2-4B9C-96B0-6ED4BFD6437E';
type WeightNode = { name: string; weight: number; children: WeightNode[]; id?: string; container?: boolean };
type Mode = 'whole' | 'on_hand' | 'container';
function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}
const unwrap = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
function num(value: unknown, fallback = 0): number {
    const n = Number(unwrap(value));
    return Number.isFinite(n) ? n : fallback;
}
const round2 = (value: number): number => Math.round(value * 100) / 100;
const isPrivate = (entry: unknown): boolean => num(ftInventoryField(entry, 'inventory_private')) === 2;
const rowEncumbranceLb = (entry: InventoryEntry): number => inventoryRowTotals(entry).grossLb;
function statusHas(entry: unknown, id: string): boolean {
    return ftCreatureStatuses(entry, 'target').some(status => String(ftItemsCol(status, 'item_id') ?? '').toUpperCase() === id);
}
function makeNumberPretty(value: number): string {
    return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/**
 * Capacity threshold (lb). The value PHP reads via layer_value('creature_*_carrying_capacity') rides in
 * the computed cluster at computed.creature.capacity.<cluster>.total; fall back to the flat layer for
 * legacy blobs (mirrors the header carry-weight strip in tabs/inventory.ts).
 */
function capTotal(caster: CachedCaster, cluster: string, layerName: string): number {
    const v = computedCreature(caster, 'capacity', cluster);
    if (v !== undefined && v !== null) return num(v);
    return num(ftLayerValue(caster, layerName));
}

// ── HTML assembly ──
function pctFnFactory(maxScale: number): (lb: number) => number {
    return (lb: number) => (maxScale > 0 ? Math.min(100, (lb / maxScale) * 100) : 0);
}

/** Vertical load meter (current fill + threshold lines). nowBadgeClasses tints the "lb now" badge by load. */
function buildMeter(
    meterLabel: string,
    currentLb: number,
    lines: string,
    nowBadgeClasses: string,
): string {
    return `<div class="d-flex flex-column align-items-center justify-content-between py-1" style="flex: 0 0 auto;">`
        + `<div class="small text-muted text-center mb-1">${escapeHtml(meterLabel)}</div>`
        + `<div class="position-relative border border-secondary rounded bg-dark" style="width: 40px; height: 200px;" title="Current: ${escapeHtml(String(round2(currentLb)))} lbs">`
        + lines
        + `</div>`
        + `<div class="small text-center mt-1" style="max-width: 6.5rem;">`
        + `<div><span class="badge ${nowBadgeClasses}">${escapeHtml(String(round2(currentLb)))}</span> lb now</div>`
        + `</div></div>`;
}

function currentFill(pctCurrent: number): string {
    return `<div class="position-absolute bottom-0 start-0 end-0 rounded-bottom bg-info opacity-75" style="height: ${pctCurrent}%;"></div>`;
}

function thresholdLine(border: string, pct: number, title: string): string {
    return `<div class="position-absolute start-0 end-0 border-top ${border}" style="bottom: ${pct}%; z-index: 2;" title="${escapeHtml(title)}"></div>`;
}

/** Final panel shell (#ft-inv-weight-root) — pie canvas + meter + top-list + thresholds list. */
function buildPanelShell(
    treeRoot: WeightNode,
    startBreadcrumb: string,
    panelTitle: string,
    meterHtml: string,
    bottomList: string,
): string {
    const treeJson = escapeHtml(JSON.stringify(treeRoot));
    const crumb = escapeHtml(startBreadcrumb);
    return `<div id="ft-inv-weight-root" class="p-2" data-tree="${treeJson}" data-start-breadcrumb="${crumb}">`
        + `<div class="d-flex justify-content-between align-items-center rounded p-2 mb-2 bg-info text-dark bg-gradient border border-dark">`
        + `<h5 class="mb-0 d-flex align-items-center gap-2">${iconMap('weight')} ${escapeHtml(panelTitle)}</h5>`
        + `<div class="d-flex align-items-center gap-2">`
        + `<button type="button" id="ft-inv-weight-open-inv" class="btn btn-sm btn-light border border-dark d-none" onclick="ftInvWeightOpenCurrentContainer();" aria-label="Open this container in the Inventory tab">${iconMap('inventory', 25, 25, 'Open this container in the Inventory tab')}</button>`
        + `<button type="button" id="ft-inv-weight-back" class="btn btn-sm btn-light border border-dark d-none" onclick="ftInvWeightGoUp();" aria-label="Back up one level">${iconMap('back', 25, 25, 'Back up one level')}</button>`
        + `</div>`
        + `</div>`
        + `<div class="small text-muted mb-2" id="ft-inv-weight-breadcrumb">${crumb}</div>`
        + `<div class="d-flex flex-row gap-2 align-items-stretch justify-content-center">`
        + `<div class="flex-grow-1" style="min-width: 0; max-width: 100%;">`
        + `<div class="position-relative d-flex justify-content-center mb-2" style="height: 220px;">`
        + `<canvas id="ftInvWeightPieCanvas" height="220" style="display: block; max-width: 100%;"></canvas>`
        + `</div>`
        + `<div class="small text-center text-muted" id="ft-inv-weight-hint">Click a slice to open a further breakdown when available.</div>`
        + `</div>`
        + meterHtml
        + `</div>`
        + `<div class="mt-2 pt-2 border-top d-none" id="ft-inv-weight-top-list-section">`
        + `<div class="small text-muted mb-1">Top 10 at this level</div>`
        + `<div id="ft-inv-weight-top-list-host"></div>`
        + `</div>`
        + `<ul class="list-unstyled small mb-0 mt-3 border-top pt-2">${bottomList}</ul>`
        + `</div>`;
}

// ── the three modes ──
/**
 * Group one bucket of leaf rows (Equipped / On Hand) into a top-level slice: weight = sum of each row's
 * encumbrance, children = each row (heaviest first) so the slice drills down to its items.
 */
function groupNode(name: string, rows: InventoryEntry[]): WeightNode {
    const weight = round2(rows.reduce((s, r) => s + rowEncumbranceLb(r), 0));
    const children = rows.map(row => buildWeightNode(row)).sort((a, b) => b.weight - a.weight);
    return { name, weight, children };
}

/**
 * Whole-creature breakdown grouped by LOCATION, not equipment slot: top-level slices are Equipped
 * (worn non-container gear), On Hand (loose non-container gear), then one slice per container — each
 * drilling into its contents heaviest-first. Containers are pulled out of Equipped/On Hand so every
 * top-level row lands in exactly one bucket (no double-count); the slices still sum to total_weight_held.
 */
function buildWhole(caster: CachedCaster, inv: InventoryEntry[]): string | null {

    const equippedItems: InventoryEntry[] = [];
    const onHandItems: InventoryEntry[] = [];
    const containerNodes: WeightNode[] = [];

    for (const row of inv) {
        if (!row || typeof row !== 'object') continue;
        if (isPrivate(row) || isIntegratedMonsterBit(row)) continue;
        if (isContainerEntry(row)) {
            // Each container becomes its own slice (buildWeightNode → contents nested, heaviest first).
            containerNodes.push(buildWeightNode(row));
            continue;
        }
        if (equippedSlotId(row) !== '') {
            equippedItems.push(row);
        } else {
            onHandItems.push(row);
        }
    }

    const topChildren: WeightNode[] = [];
    if (equippedItems.length) topChildren.push(groupNode('Equipped', equippedItems));
    if (onHandItems.length) topChildren.push(groupNode('On Hand', onHandItems));
    containerNodes.sort((a, b) => b.weight - a.weight);
    topChildren.push(...containerNodes);

    const creatureName = String(
        unwrap(caster.encounter_creatures?.encounter_creature_name) ??
        unwrap(ftItemsCol(caster, 'item_name')) ??
        'Creature',
    );

    const totalTrees = topChildren.reduce((s, n) => s + n.weight, 0);
    const totalHeldRaw =
        caster.computed?.creature?.weight?.total_weight_held ??
        caster.total_weight_held ??
        null;
    const totalHeld = totalHeldRaw !== null ? num(totalHeldRaw) : null;

    const treeRoot: WeightNode = {
        name: creatureName,
        weight: round2(totalHeld ?? totalTrees),
        children: topChildren,
    };

    const currentLb = totalHeld ?? totalTrees;
    return finishThreshold(treeRoot, caster, currentLb, 'Equipped, on hand, and containers', 'Carry Weight Breakdown', 'Carry load');
}

function buildOnHand(caster: CachedCaster, inv: InventoryEntry[], ecId: string): string | null {
    const filtered = onHandRows(inv, ecId);

    const children = filtered.map(row => buildWeightNode(row));
    children.sort((a, b) => b.weight - a.weight);
    const twSum = filtered.reduce((s, r) => s + rowEncumbranceLb(r), 0);

    const treeRoot: WeightNode = { name: 'On hand', weight: round2(twSum), children };
    return finishThreshold(
        treeRoot,
        caster,
        round2(twSum),
        'Unequipped items carried directly (nested bag contents included)',
        'On-Hand Weight Breakdown',
        'On-hand vs carry limits',
    );
}

/** Shared tail for whole + on-hand modes: encumbrance-threshold meter + thresholds list. */
function finishThreshold(
    treeRoot: WeightNode,
    caster: CachedCaster,
    currentLb: number,
    startBreadcrumb: string,
    panelTitle: string,
    meterLabel: string,
): string {
    const encLb = capTotal(caster, 'encumbered', 'creature_encumbered_carrying_capacity');
    const heavyLb = capTotal(caster, 'heavily_encumbered', 'creature_heavily_encumbered_carrying_capacity');
    const immobileLb = capTotal(caster, 'max', 'creature_max_carrying_capacity');

    const maxScale = Math.max(currentLb, immobileLb, heavyLb, encLb, 1.0) * 1.08;
    const pct = pctFnFactory(maxScale);

    const lines = currentFill(pct(currentLb))
        + thresholdLine('border-warning', pct(encLb), `Encumbered threshold (${encLb} lb)`)
        + thresholdLine('border-danger', pct(heavyLb), `Heavily encumbered threshold (${heavyLb} lb)`)
        + thresholdLine('border-light', pct(immobileLb), `Max / restrained (immobile) threshold (${immobileLb} lb)`);

    // Value badges match their swatch colors (amber / red / dark) so the threshold legend reads as one unit.
    const bottomList =
        `<li class="d-flex justify-content-between"><span><span class="badge bg-warning border border-dark">&nbsp;</span> Encumbered at</span><span class="badge bg-warning text-dark bg-gradient border border-dark">${escapeHtml(String(encLb))} lb</span></li>`
        + `<li class="d-flex justify-content-between mt-1"><span><span class="badge bg-danger">&nbsp;</span> Heavily encumbered at</span><span class="badge bg-danger text-white bg-gradient border border-dark">${escapeHtml(String(heavyLb))} lb</span></li>`
        + `<li class="d-flex justify-content-between mt-1"><span><span class="badge bg-dark border">&nbsp;</span> Max / restrained at</span><span class="badge bg-dark text-white bg-gradient border border-light">${escapeHtml(String(immobileLb))} lb</span></li>`;

    const nowBadge = carryWeightBadgeClasses(currentLb, encLb, heavyLb, immobileLb);
    return buildPanelShell(treeRoot, startBreadcrumb, panelTitle, buildMeter(meterLabel, currentLb, lines, nowBadge), bottomList);
}

function buildContainer(caster: CachedCaster, inv: InventoryEntry[], containerInventoryId: string): string | null {
    if (!UUID_RE.test(containerInventoryId)) return null;
    const containerRow = findRowById(inv, containerInventoryId);
    if (containerRow === null) return null;

    const locked = statusHas(containerRow, ST_LOCKED) && !ftHasCampaignPermission(containerRow);

    let treeRoot: WeightNode;
    let startBreadcrumb: string;
    let currentLb: number;
    let capLb: number | null;

    if (locked) {
        const containerName = String(unwrap(ftInventoryField(containerRow, 'inventory_name')) ?? 'Container');
        treeRoot = { name: containerName, weight: 0, children: [] };
        startBreadcrumb = 'Contents: ' + containerName;
        currentLb = 0;
        capLb = null;
    } else {
        const node = buildWeightNode(containerRow);
        const held = inventoryRowTotals(containerRow).heldLb;
        treeRoot = { name: node.name, weight: round2(held), children: node.children };
        startBreadcrumb = 'Contents: ' + (node.name || 'Container');
        currentLb = held;
        const capRaw = ftLayerValue(containerRow, 'carrying_capacity_pounds');
        const capStr = String(unwrap(capRaw) ?? '');
        capLb = capStr !== '' && Number.isFinite(Number(capStr)) ? Number(capStr) : null;
    }

    const maxScale = Math.max(currentLb, capLb ?? 0, 1.0) * 1.08;
    const pct = pctFnFactory(maxScale);
    const pctCap = capLb !== null && capLb > 0 ? pct(capLb) : null;

    const lines = currentFill(pct(currentLb))
        + (pctCap !== null ? thresholdLine('border-light', pctCap, `Capacity (${round2(capLb as number)} lb)`) : '');

    const remaining = capLb !== null && capLb > 0 ? round2(capLb - currentLb) : null;
    const totals = inventoryRowTotals(containerRow);
    const grossF = makeNumberPretty(totals.grossLb);

    const bottomList =
        `<li class="d-flex justify-content-between"><span>Capacity</span><span class="badge bg-secondary bg-gradient border border-dark">${capLb !== null ? escapeHtml(String(round2(capLb))) + ' lb' : '—'}</span></li>`
        + `<li class="d-flex justify-content-between mt-1"><span>Remaining</span><span class="badge bg-secondary bg-gradient border border-dark">${remaining !== null ? escapeHtml(String(remaining)) + ' lb' : '—'}</span></li>`
        + `<li class="d-flex justify-content-between mt-1"><span>${iconMap('sum')} ${totals.fixedWeight ? 'Fixed carry weight' : 'Gross (item + contents)'}</span><span class="badge bg-dark text-white bg-gradient border border-light">${escapeHtml(String(grossF ?? '—'))}</span></li>`;

    // Over capacity → red; otherwise neutral cyan (dark text for legibility).
    const nowBadge = (capLb !== null && capLb > 0 && currentLb >= capLb)
        ? 'bg-danger text-white bg-gradient border border-dark'
        : 'bg-info text-dark bg-gradient border border-dark';
    return buildPanelShell(treeRoot, startBreadcrumb, 'Container Weight Breakdown', buildMeter('Container load', currentLb, lines, nowBadge), bottomList);
}

function buildGp(inv: InventoryEntry[], ecId: string, mode: Mode, containerId: string): string | null {
    const container = mode === 'container' ? findRowById(inv, containerId) : null;
    if (mode === 'container' && !container) return null;
    const locked = container && statusHas(container, ST_LOCKED) && !ftHasCampaignPermission(container);
    const rows = container ? locked ? [] : contentsOf(container) : onHandRows(inv, ecId);
    const children = rows.map(row => buildWeightNode(row, 'gp')).sort((a, b) => b.gp - a.gp);
    const title = container ? String(ftInventoryField(container, 'inventory_name') ?? 'Container') : 'On hand';
    const gp = children.reduce((sum, node) => sum + node.gp, 0);
    const tree = { name: title, gp: round2(gp), children };
    return `<div id="ft-inv-gp-root" class="p-2" data-tree="${escapeHtml(JSON.stringify(tree))}" data-start-breadcrumb="${escapeHtml(title)}">`
        + `<div class="d-flex justify-content-between align-items-center rounded p-2 mb-2 bg-warning text-dark border border-dark"><h5>${iconMap('cost_gp')} ${escapeHtml(title)} GP Breakdown</h5>`
        + `<button type="button" id="ft-inv-gp-back" class="btn btn-sm btn-light d-none" onclick="ftInvGpGoUp();" aria-label="Back up one level">${iconMap('back')}</button></div>`
        + `<div class="small text-muted mb-2" id="ft-inv-gp-breadcrumb">${escapeHtml(title)}</div>`
        + `<div class="position-relative" style="height:220px;"><canvas id="ftInvGpPieCanvas" height="220"></canvas></div>`
        + `<div id="ft-inv-gp-top-list-section" class="mt-2 border-top"><div class="small text-muted">Top 10 at this level</div><div id="ft-inv-gp-top-list-host"></div></div>`
        + `<div class="mt-2">Total <span class="badge bg-warning text-dark">${escapeHtml(makeNumberPretty(gp))} gp</span></div></div>`;
}

/** Existing public bridge, also used for GP and standalone container cards. A cold caller hydrates
 * through the page's cache/compiler owner and retries; no PHP rule endpoint is involved. */
export function ftRenderInventoryWeightBreakdown(casterId: string, mode: Mode, containerInventoryId = '', metric: 'weight' | 'gp' = 'weight', resolvedContainer?: Record<string, unknown>): string | null {
    const caches = window.FateTide as unknown as { casterCache?: Map<string, unknown>; objectCache?: Map<string, unknown> };
    const held = (id: string): Record<string, unknown> | null => {
        for (const cache of [caches?.casterCache, caches?.objectCache]) {
            const row = recordOf(cache?.get(id) ?? cache?.get(id.toUpperCase()));
            if (row) return row;
        }
        return null;
    };
    let caster = held(casterId) as CachedCaster | null;
    let inv = inventoryRows(caster?.inventory);
    if (mode === 'container' && !findRowById(inv, containerInventoryId)) {
        // A card can be opened independently of the active caster (placed props, ability-held bags).
        const standalone = resolvedContainer ?? held(containerInventoryId);
        if (standalone && !standalone._inventory_refusal && !standalone._entity_refusal
            && String(ftInventoryField(standalone, 'inventory_id') ?? '').toUpperCase() === containerInventoryId.toUpperCase()) inv = [standalone];
    }
    if (mode !== 'container' && (caster?._inventory_refusal || caster?._entity_refusal)) return null;
    if (!caster && mode === 'container' && inv.length) caster = {} as CachedCaster;
    if (!caster) return null;
    const ecId = String(unwrap(caster.encounter_creatures?.encounter_creature_id) ?? '');
    if (metric === 'gp') return buildGp(inv, ecId, mode, containerInventoryId);
    if (mode === 'container') return buildContainer(caster, inv, containerInventoryId);
    if (mode === 'on_hand') return buildOnHand(caster, inv, ecId);
    return buildWhole(caster, inv);
}
